import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Text, Title, Stack, Group, Box, SimpleGrid, Container } from '@mantine/core';
import { IconBluetooth, IconSettings, IconUsb } from '@tabler/icons-react';
import { useViewportSize } from '@mantine/hooks';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../styles/pages/ConnectPage.scss';
import { useTranslation } from 'react-i18next';

const ConnectPage: React.FC = () => {
  const navigate = useNavigate();
  const { width } = useViewportSize();
  const isMobile = width < 768;
  const { t } = useTranslation();
  
  const [isDevMode, setIsDevMode] = useState<boolean>(false);
  const [showDevModeHint, setShowDevModeHint] = useState<boolean>(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift' && !isDevMode) {
        setShowDevModeHint(true);
        timer = setTimeout(() => {
          setIsDevMode(true);
          setShowDevModeHint(false);
        }, 3000);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        clearTimeout(timer);
        setShowDevModeHint(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      clearTimeout(timer);
    };
  }, [isDevMode]);

  const handleDeviceSelect = (deviceType: 'gateway' | 'sensor') => {
    navigate(`/${deviceType}`);
  };
  
  return (
    <Container size="lg" py="xl" className="connect-page">
      <Stack align="center" gap="xl">
        <Title order={1} ta="center">{t('connectPage.title')}</Title>
        <Text size="lg" c="dimmed" ta="center">
          {t('connectPage.subtitle')}
        </Text>
        
        <SimpleGrid 
          cols={isMobile ? 1 : 2} 
          spacing="lg"
          style={{ width: '100%', maxWidth: '800px' }}
        >
          {/* Gateway Card */}
          <Card 
            shadow="sm" 
            p="lg" 
            radius="md"
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() => handleDeviceSelect('gateway')}
          >
            <Stack align="center" gap="md">
              <Box className="card-icon gateway" p="md" mb="sm">
                <IconUsb size={48} />
              </Box>
              <Title order={3} ta="center">{t('connectPage.gatewayTitle')}</Title>
              <Text c="dimmed" ta="center">
                {t('connectPage.gatewayDesc')} (USB)
              </Text>
              <Button fullWidth mt="md" radius="md">
                {t('connectPage.connect')}
              </Button>
            </Stack>
          </Card>
          
          {/* Sensor Card */}
          <Card 
            shadow="sm" 
            p="lg" 
            radius="md"
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() => handleDeviceSelect('sensor')}
          >
            <Stack align="center" gap="md">
              <Box className="card-icon sensor" p="md" mb="sm">
                <IconBluetooth size={48} />
              </Box>
              <Title order={3} ta="center">{t('connectPage.sensorTitle')}</Title>
              <Text c="dimmed" ta="center">
                {t('connectPage.sensorDesc')}
              </Text>
              <Button variant="outline" fullWidth mt="md" radius="md">
                {t('connectPage.connect')}
              </Button>
            </Stack>
          </Card>
        </SimpleGrid>
        
        {isDevMode && (
          <Box mt="xl" style={{ width: '100%', maxWidth: '800px' }}>
            <Title order={3} mb="md">{t('connectPage.developerOptions')}</Title>
            <Group justify="center" gap="md">
              <Button 
                variant="outline" 
                leftSection={<IconSettings size={16} />}
                onClick={() => {/* TODO: Open settings */}}
                radius="md"
              >
                {t('connectPage.settings')}
              </Button>
              <Button 
                variant="outline" 
                leftSection={<IconSettings size={16} />}
                onClick={() => {/* TODO: Show logs */}}
                radius="md"
              >
                {t('connectPage.showLogs')}
              </Button>
            </Group>
          </Box>
        )}
        
        {showDevModeHint && (
          <Box 
            p="md" 
            mt="md" 
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.7)', 
              color: 'white',
              borderRadius: '4px',
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000
            }}
          >
            {t('connectPage.devModeHint')}
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default ConnectPage;
